import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  TextField,
  Typography,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Content, Page } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';

interface PortalApplication {
  id: string;
  name: string;
  description?: string;
  status?: string;
  type?: string;
  clientId?: string;
  owner?: { displayName?: string; email?: string };
  createdAt?: string;
  updatedAt?: string;
  consoleUrl: string;
}

interface LookupResult {
  matchedOn: 'id' | 'clientId';
  application: PortalApplication;
}

const ERROR_MESSAGES: Record<string, string> = {
  invalid_identifier: 'That does not look like an application id or client id.',
  not_found: 'No application matches that identifier.',
  upstream_unavailable: 'Gravitee is unreachable right now. Try again shortly.',
  not_configured: 'Application lookup is not configured on this environment.',
};

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleString() : 'Unknown';

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box mb={2}>
    <Typography variant="caption" color="textSecondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" component="div">
      {children}
    </Typography>
  </Box>
);

export const ApplicationSearchPage = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LookupResult | null>(null);

  const search = useCallback(async () => {
    const identifier = input.trim();
    if (!identifier) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const base = await discoveryApi.getBaseUrl('gravitee-applications');
      const response = await fetchApi.fetch(
        `${base}/lookup/${encodeURIComponent(identifier)}`,
      );
      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(ERROR_MESSAGES[body?.error] ?? `Lookup failed (${response.status})`);
        return;
      }
      setResult(body as LookupResult);
    } catch {
      setError('Lookup failed. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi, input]);

  return (
    <Page themeId="tool">
      <Content>
        <Box display="flex" alignItems="center" style={{ gap: 12 }} mb={3} maxWidth={720}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Application id or client id"
            placeholder="271b7f47-c7be-4fca-9b7f-47c7be0fca15"
            value={input}
            onChange={event => setInput(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') search();
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
            disabled={loading || !input.trim()}
            onClick={search}
          >
            Search
          </Button>
        </Box>

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {result && (
          <Card variant="outlined" style={{ maxWidth: 720 }}>
            <CardContent>
              <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={1}>
                <Typography variant="h6" style={{ wordBreak: 'break-all' }}>
                  {result.application.name}
                </Typography>
                {result.application.status && (
                  <Chip
                    size="small"
                    label={result.application.status}
                    color={result.application.status === 'ACTIVE' ? 'primary' : 'default'}
                  />
                )}
              </Box>

              <Typography variant="caption" color="textSecondary">
                Matched on {result.matchedOn === 'id' ? 'application id' : 'client id'}
              </Typography>

              {result.application.description && (
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  {result.application.description}
                </Typography>
              )}

              <Box my={2}>
                <Divider />
              </Box>

              <Field label="Owner">
                {result.application.owner?.email ? (
                  <Link href={`mailto:${result.application.owner.email}`}>
                    {result.application.owner.displayName ?? result.application.owner.email}
                  </Link>
                ) : (
                  'Unknown'
                )}
              </Field>

              <Field label="Application id">
                <code style={{ wordBreak: 'break-all' }}>{result.application.id}</code>
              </Field>

              <Field label="Client id">
                <code style={{ wordBreak: 'break-all' }}>
                  {result.application.clientId ?? 'None'}
                </code>
                {result.matchedOn === 'id' && result.application.clientId && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    Hidden — search by the full client id to reveal it
                  </Typography>
                )}
              </Field>

              <Field label="Type">{result.application.type ?? 'Unknown'}</Field>
              <Field label="Created">{formatDate(result.application.createdAt)}</Field>

              <Button
                variant="outlined"
                size="small"
                endIcon={<OpenInNewIcon />}
                href={result.application.consoleUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Gravitee console
              </Button>
            </CardContent>
          </Card>
        )}
      </Content>
    </Page>
  );
};
